import { useEffect, useLayoutEffect, useRef, useState } from "react";
import * as Progress from "@radix-ui/react-progress";
import logo from "../../assets/logo/logo.png";
import { useTenantSplashColors } from "api/institute";

// Color palette - easily modifiable
const COLORS = [
  "#93c5fd", // lightBlue
  "#60a5fa", // blue
  "#67e8f9", // cyan
  "#818cf8", // indigo
  "#f9b71f", // yellow
  "#38bdf8", // skyBlue
  "#2563eb", // darkBlue
  "#a5b4fc", // lightIndigo
  "#fbbf24", // amber
];

// Loading bar colors - easily modifiable
const LOADING_COLORS = [
  "#DBEAFE", // bg-blue-100 (background)
  "#3B82F6", // bg-blue-500 (indicator)
];

function useAnimatedBlobs(blob: string[], loading_indicator: string[]) {
  const BLOB_CONFIGS = [
    {
      base: {
        x: 0.1,
        y: 0.1,
        size: 0.6,
        color: blob[0],
        opacity: 0.6,
        blur: 80,
      },
      speed: 0.38,
      amp: 600,
      freq: 2.2,
      phase: Math.random() * Math.PI * 2,
    },
    {
      base: {
        x: 0.7,
        y: 0.7,
        size: 0.5,
        color: blob[1],
        opacity: 0.5,
        blur: 100,
      },
      speed: 0.33,
      amp: 700,
      freq: 2.1,
      phase: Math.random() * Math.PI * 2,
    },
    {
      base: {
        x: 0.5,
        y: 0.3,
        size: 0.4,
        color: blob[2],
        opacity: 0.4,
        blur: 60,
      },
      speed: 0.35,
      amp: 650,
      freq: 2.3,
      phase: Math.random() * Math.PI * 2,
    },
    {
      base: {
        x: 0.2,
        y: 0.8,
        size: 0.3,
        color: blob[3],
        opacity: 0.4,
        blur: 60,
      },
      speed: 0.31,
      amp: 800,
      freq: 2.2,
      phase: Math.random() * Math.PI * 2,
    },
    // Subtle yellow accent blob
    {
      base: {
        x: 0.65,
        y: 0.25,
        size: 0.18,
        color: blob[4],
        opacity: 0.18,
        blur: 40,
      },
      speed: 0.36,
      amp: 520,
      freq: 2.4,
      phase: Math.random() * Math.PI * 2,
    },
    // Extra blue/cyan blobs for more movement
    {
      base: {
        x: 0.8,
        y: 0.15,
        size: 0.22,
        color: blob[5],
        opacity: 0.22,
        blur: 50,
      },
      speed: 0.39,
      amp: 640,
      freq: 2.25,
      phase: Math.random() * Math.PI * 2,
    },
    {
      base: {
        x: 0.3,
        y: 0.7,
        size: 0.19,
        color: blob[6],
        opacity: 0.18,
        blur: 40,
      },
      speed: 0.37,
      amp: 610,
      freq: 2.35,
      phase: Math.random() * Math.PI * 2,
    },
    // Two more blobs for extra movement
    {
      base: {
        x: 0.4,
        y: 0.5,
        size: 0.21,
        color: blob[7],
        opacity: 0.16,
        blur: 55,
      },
      speed: 0.41,
      amp: 720,
      freq: 2.5,
      phase: Math.random() * Math.PI * 2,
    },
    {
      base: {
        x: 0.6,
        y: 0.2,
        size: 0.17,
        color: blob[8],
        opacity: 0.13,
        blur: 45,
      },
      speed: 0.43,
      amp: 680,
      freq: 2.6,
      phase: Math.random() * Math.PI * 2,
    },
  ];
  const [blobs, setBlobs] = useState(() =>
    BLOB_CONFIGS.map((cfg) => ({
      x: cfg.base.x,
      y: cfg.base.y,
      scale: 1,
      borderRadius: "50% 50% 50% 50% / 50% 50% 50% 50%",
    }))
  );
  const start = useRef(Date.now());

  useEffect(() => {
    let frame: number;
    let isMounted = true;
    
    function animate() {
      if (!isMounted) return;
      
      const now = Date.now();
      const t = (now - start.current) / 1000;
      setBlobs(
        BLOB_CONFIGS.map((cfg, i) => {
          const angle = t * cfg.speed + i + cfg.phase;
          const x =
            cfg.base.x + (Math.sin(angle * cfg.freq + i) * cfg.amp) / 1000;
          const y =
            cfg.base.y +
            (Math.cos(angle * cfg.freq * 0.8 + i) * cfg.amp) / 1000;
          const scale =
            1 +
            Math.sin(angle * 0.7 + i) * 0.55 +
            Math.cos(angle * 0.5 - i) * 0.45;
          // Even more dramatic morphing border-radius
          const r1 = 50 + Math.sin(angle * 1.2) * 55;
          const r2 = 50 + Math.cos(angle * 1.5) * 48;
          const r3 = 50 + Math.sin(angle * 1.7 + 1) * 52;
          const r4 = 50 + Math.cos(angle * 1.3 - 1) * 46;
          const r5 = 50 + Math.sin(angle * 1.4 + 2) * 51;
          const r6 = 50 + Math.cos(angle * 1.6 - 2) * 49;
          const r7 = 50 + Math.sin(angle * 1.8 + 3) * 53;
          const r8 = 50 + Math.cos(angle * 1.1 - 3) * 47;
          const borderRadius = `${r1}% ${r2}% ${r3}% ${r4}% / ${r5}% ${r6}% ${r7}% ${r8}%`;
          return { x, y, scale, borderRadius };
        })
      );

      frame = requestAnimationFrame(animate);
    }
    frame = requestAnimationFrame(animate);
    
    return () => {
      isMounted = false;
      if (frame) {
        cancelAnimationFrame(frame);
      }
    };
  }, [blob]);
  return { blobs, BLOB_CONFIGS };
}

function SplashScreen({
  onComplete,
  duration = 1500,
  loading_indicator,
  blob,
}: {
  onComplete: () => void;
  duration?: number;
  loading_indicator: string[];
  blob: string[];
}) {
  const [isVisible, setIsVisible] = useState(true);
  const [progress, setProgress] = useState(0);
  const progressRef = useRef(0);
  const { blobs, BLOB_CONFIGS } = useAnimatedBlobs(blob, loading_indicator);
  const [done, setDone] = useState(false);

  // Ultra-smooth progress bar using requestAnimationFrame and ref
  useEffect(() => {
    let frame: number;
    let isMounted = true;
    const startTime = performance.now();
    
    function animate(now: number) {
      if (!isMounted) return;
      
      const elapsed = now - startTime;
      const percent = Math.max(0, Math.min((elapsed / duration) * 100, 100));
      if (Math.abs(percent - progressRef.current) >= 0.5 || percent === 100) {
        progressRef.current = percent;
        setProgress(percent);
      }
      if (percent < 100) {
        frame = requestAnimationFrame(animate);
      } else {
        setDone(true);
      }
    }
    frame = requestAnimationFrame(animate);
    
    return () => {
      isMounted = false;
      if (frame) {
        cancelAnimationFrame(frame);
      }
    };
  }, [duration]);

  // Fade out after progress reaches 100%
  useEffect(() => {
    if (done) {
      const timeout1 = setTimeout(() => {
        setIsVisible(false);
        const timeout2 = setTimeout(() => {
          onComplete();
        }, 500);
        
        // Store timeout2 for cleanup if needed
        return () => clearTimeout(timeout2);
      }, 200); // allow progress bar to visually finish
      
      return () => clearTimeout(timeout1);
    }
  }, [done, onComplete]);

  return (
    <div
      className={`splash-screen fixed inset-0 z-50 flex items-center justify-center transition-opacity duration-500 ${
        isVisible ? "opacity-100" : "opacity-0 pointer-events-none"
      }`}
      style={{
        overflow: "hidden",
        background: "linear-gradient(120deg, #e0f2fe 0%, #bae6fd 100%)",
      }}
    >
      {/* Animated Fluid Gradient Mesh Background */}
      <div className="absolute inset-0 w-full h-full -z-10">
        {BLOB_CONFIGS.map((cfg, i) => {
          const blob = blobs[i];
          const left = `calc(${blob.x * 100}% - ${cfg.base.size * 50}vw)`;
          const top = `calc(${blob.y * 100}% - ${cfg.base.size * 50}vw)`;
          const size = `${cfg.base.size * 100}vw`;
          return (
            <div
              key={i}
              style={{
                position: "absolute",
                left,
                top,
                width: size,
                height: size,
                background: cfg.base.color,
                opacity: cfg.base.opacity,
                borderRadius: blob.borderRadius,
                filter: `blur(${cfg.base.blur}px)`,
                transform: `scale(${blob.scale})`,
                transition: "background 0.3s",
                willChange: "transform, border-radius",
                pointerEvents: "none",
              }}
            />
          );
        })}
      </div>
      <div className="splash-content flex flex-col items-center gap-6">
        <div className="logo-container flex flex-col items-center gap-2">
          <div className="logo-icon w-24 h-24 flex items-center justify-center">
            <img
              src={logo}
              alt="Nexme Logo"
              width={96}
              height={0}
              className="object-contain"
              style={{ height: "auto" }}
            />
          </div>
        </div>
        <div className="loading-container w-64 flex flex-col items-center gap-2">
          <Progress.Root
            className="progress-root w-full h-2 rounded overflow-hidden"
            style={{
              background: loading_indicator[0],
            }}
            value={progress}
            max={100}
          >
            <Progress.Indicator
              className="progress-indicator h-2 transition-transform duration-200"
              style={{
                transition: "transform 0.2s linear",
                transform: `translateX(-${100 - progress}%)`,
                background: loading_indicator[1],
              }}
            />
          </Progress.Root>
        </div>
      </div>
    </div>
  );
}

export default function ClientLayout({
  children,
}: {
  children: React.ReactNode;
}) {
  const [showSplash, setShowSplash] = useState(true);
  const [loading, setLoading] = useState(LOADING_COLORS);
  const [blob, setBlob] = useState(COLORS);
  const { data: splashData, isLoading: loadingSplash } =
    useTenantSplashColors();

  useLayoutEffect(() => {
    if (loadingSplash || !splashData) return;
    if (splashData?.blobs) {
      splashData.blobs = splashData.blobs.map(
        (color: string, index: number) => {
          return !color ? COLORS[index] : color;
        }
      );
    }
    if (splashData?.loading_indicator) {
      splashData.loading_indicator = splashData.loading_indicator.map(
        (color: string, index: number) => {
          return !color ? LOADING_COLORS[index] : color;
        }
      );
    }
    setBlob(splashData.blobs || COLORS);
    setLoading(splashData.loading_indicator || LOADING_COLORS);
  }, [splashData, loadingSplash]);

  return (
    <>
      {showSplash && !loadingSplash && (
        <SplashScreen
          onComplete={() => setShowSplash(false)}
          loading_indicator={loading}
          blob={blob}
        />
      )}
      <div style={{ visibility: showSplash ? "hidden" : "visible" }}>
        {children}
      </div>
    </>
  );
}
